// import LanguageChanger from "@/components/LanguageChanger/languageChanger.component";
import MagicLink from "@/components/Login/magic-link.component";
import { ROUTE_HOME } from "@/constants/routes.constants";
import { mfaLogin } from "@/models/api/authenticate.model";
import { Button } from "@/shadcn/components/ui/button";
import { Input } from "@/shadcn/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/shadcn/components/ui/tabs";
import { encodedMFAConfirm } from "@/utils/encoding.util";
import { setTokensLocalStorage } from "@/utils/tokens.util";
import { isLanguageSupportEnabled } from "@/utils/utils.util";
import { validateEmail } from "@/utils/validation.util";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  const languageSupportEnabled = isLanguageSupportEnabled();

  function validateFormLogin(): { email: string; password: string } | false {
    const strippedEmail = email.replace(/\s/g, "");
    const strippedPassword = password.replace(/\s/g, "");
    if (!strippedEmail || strippedPassword === "") {
      setError(t("Please fill in all fields"));
      return false;
    }

    if (!validateEmail(strippedEmail)) {
      setError(t("Please enter a valid email"));
      return false;
    }

    return { email: strippedEmail, password: strippedPassword };
  }

  async function handleLogin() {
    setError("");
    const validatedForm = validateFormLogin();
    if (!validatedForm) return;

    setLoggingIn(true);

    // Encode email and password
    const encoded = encodedMFAConfirm(
      validatedForm.email,
      validatedForm.password
    );

    if (!encoded) {
      setError(t("Failed to encode email and password"));
      setLoggingIn(false);
      return;
    }

    const response = await mfaLogin(encoded);
    console.log(response);
    if (response.rStatus === "error") {
      setError(response.message);
      setLoggingIn(false);
    } else {
      setTokensLocalStorage(response.tokens);
      window.location.href = ROUTE_HOME;
    }
  }

  const handleTabChange = () => {
    setError("");
    setPassword("");
  };

  return (
    <>
      <Tabs defaultValue="magic-link" className="w-full">
        <TabsList className="bg-input/30 mb-2 grid w-full grid-cols-2 items-center gap-5">
          <TabsTrigger value="magic-link" onClick={() => handleTabChange()}>
            {t("Magic Link")}
          </TabsTrigger>
          <TabsTrigger value="login" onClick={() => handleTabChange()}>
            {t("Login")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="magic-link">
          <MagicLink email={email} setEmail={setEmail} />
        </TabsContent>
        <TabsContent value="login" className="grid w-full gap-3">
          <div className="grid grid-cols-1 items-center">
            <Input
              type="text"
              className="w-full p-1 ps-2"
              id="email"
              placeholder={t("Email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoCapitalize="off"
            />
          </div>
          <div className="grid grid-cols-1 items-center">
            <Input
              type="password"
              className="w-full p-1 ps-2"
              id="password"
              placeholder={t("Password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleLogin();
                }
              }}
            />
          </div>
          {/* {languageSupportEnabled && (
            <div className="grid grid-cols-1 items-center">
              <LanguageChanger />
            </div>
          )} */}
          {error && <div className="text-sm text-red-500">{error}</div>}
          <Button
            onClick={handleLogin}
            className="w-full bg-neutral-800 text-gray-200 hover:bg-neutral-700"
            disabled={email === "" || password === "" || loggingIn}
          >
            {loggingIn ? (
              <div className="flex items-center justify-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-t-2 border-white"></span>
                {t("Logging in...")}
              </div>
            ) : (
              t("Login")
            )}
          </Button>
        </TabsContent>
      </Tabs>
    </>
  );
};

export default Login;
